import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { backendApiService } from "../../lib/services/backendApiService";
import { updateUserAttribute } from "../../lib/services/authService";
import { EmailVerificationModal } from "../molecules/EmailVerificationModal";

export const ProfileTab: React.FC = () => {
  const { user, refreshAuth } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [newEmail, setNewEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");

  const handleUpdateProfile = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage("");

    try {
      await backendApiService.updateUserProfile(name);
      setSuccessMessage("Profile updated successfully!");
      await refreshAuth();
    } catch (err: any) {
      setError(err.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage("");

    try {
      // Use Amplify directly to update email attribute, which triggers Cognito to send verification code
      await updateUserAttribute("email", newEmail);

      // Store the pending email and open verification modal
      setPendingEmail(newEmail);
      setShowVerificationModal(true);
      setNewEmail("");
    } catch (err: any) {
      setError(err.message || "Failed to update email");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerified = async () => {
    setSuccessMessage("Email verified and updated successfully!");
    setPendingEmail("");
    // Refresh auth to get updated user info
    await refreshAuth();
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 3 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "primary.main", mb: 3 }}
          >
            Profile Information
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Name Field */}
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />

            <Button
              variant="contained"
              onClick={handleUpdateProfile}
              disabled={loading || !name || name === user?.name}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : "Update Name"}
            </Button>

            {/* Current Email Display */}
            <TextField
              fullWidth
              label="Current Email"
              value={user?.email || ""}
              disabled
              helperText="Email is used for login"
            />

            {/* Email Change Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Change Email Address
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Changing your email requires verification. You'll receive a
                verification code at your new email address.
              </Alert>

              <TextField
                fullWidth
                label="New Email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <Button
                variant="outlined"
                onClick={handleUpdateEmail}
                disabled={loading || !newEmail}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : "Update Email"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        open={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        email={pendingEmail}
        onVerified={handleEmailVerified}
        disableClose={false}
        isEmailChange={true}
      />
    </Box>
  );
};
